import {
    formatAddress,
    formatMoney,
    formatRole,
    hasNonEmptyArray,
    parseHandelsregisterOrganizationData,
} from "src/modules/3rd-party-management/pages/admin/suppliers/details/tabs-content/kyb/components/company-details/drivers/handelsregister/helpers";
import {
    buildReportHeaderSection,
    formatI18nLabel,
    formatPdfDate,
    formatPdfDisplay,
    formatPdfYesNo,
    keyValueSection,
    recordsSection,
} from "./providerPdfShared";

const formatPct = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return String(value);
    }
    return `${num.toFixed(2)}%`;
};

const formatRatio = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return String(value);
    }
    const pct = num <= 1 ? num * 100 : num;
    return `${pct.toFixed(2)}%`;
};

const formatShareholderName = (entry) => {
    const shareholder = entry?.shareholder || {};
    if (shareholder.name) {
        return shareholder.name;
    }
    return [shareholder.first_name, shareholder.last_name].filter(Boolean).join(" ") || "-";
};

const formatShareholderRole = (entry) => (
    entry?.role?.en?.long
    || entry?.role?.de?.long
    || entry?.role?.label
    || "-"
);

const formatShareholderAddress = (address) => (
    typeof address === "string" ? address : "-"
);

const formatHistoryDetails = (item) => {
    if (typeof item?.details?.value === "string") {
        return item.details.value;
    }
    return item?.details?.linked_entities?.organization?.name || "-";
};

const formatMaRole = (role) => {
    if (!role) {
        return "-";
    }
    if (role.en) {
        const en = formatI18nLabel(role.en);
        if (en !== "-") {
            return en;
        }
    }
    if (role.de) {
        const de = formatI18nLabel(role.de);
        if (de !== "-") {
            return de;
        }
    }
    return role.label || "-";
};

/**
 * Build PDF sections matching the Handelsregister KYB detail page.
 * Omits network graph visuals; includes tabular network connections.
 */
export const buildHandelsregisterExportSections = (company, header) => {
    const data = parseHandelsregisterOrganizationData(company);
    if (!data) {
        return [];
    }

    const sections = [];
    const registration = data.registration || {};
    const capital = data.capital?.current;

    const headerSection = buildReportHeaderSection(header);
    if (headerSection) {
        sections.push(headerSection);
    }

    const companyRows = [
        { title: "Company Name", details: formatPdfDisplay(data.name) },
        { title: "Status", details: formatPdfDisplay(data.status) },
        { title: "Legal Form", details: formatPdfDisplay(data.legal_form) },
        {
            title: "Commercial Register",
            details: [
                [registration.register_type, registration.register_number]
                    .filter(Boolean)
                    .join(" "),
                registration.court ? `(${registration.court})` : null,
            ].filter(Boolean).join(" ") || "-",
        },
        { title: "Registration Date", details: formatPdfDate(data.registration_date) },
        {
            title: "Registered Capital",
            details: capital
                ? `${formatMoney(capital.amount, capital.currency)}${capital.kind ? ` (${capital.kind})` : ""}`
                : "-",
        },
        { title: "Address", details: formatAddress(data.address) },
        { title: "Website", details: formatPdfDisplay(data.contact_data?.website) },
        { title: "Phone", details: formatPdfDisplay(data.contact_data?.phone_number) },
        { title: "Entity ID", details: formatPdfDisplay(data.entity_id) },
    ];

    if (data.purpose) {
        companyRows.push({ title: "Purpose", details: data.purpose });
    }

    sections.push(keyValueSection("Company Information", companyRows));

    const currentPeople = data?.related_persons?.current || [];
    const pastPeople = data?.related_persons?.past || [];
    if (hasNonEmptyArray(currentPeople) || hasNonEmptyArray(pastPeople)) {
        sections.push(recordsSection(
            "Management / Related Persons",
            [
                { key: "status", label: "Status", width: "12%" },
                { key: "name", label: "Name", width: "22%" },
                { key: "role", label: "Role", width: "18%" },
                { key: "birthDate", label: "Date of Birth", width: "14%" },
                { key: "city", label: "City", width: "12%" },
                { key: "from", label: "From", width: "11%" },
                { key: "to", label: "To", width: "11%" },
            ],
            [
                ...currentPeople.map((person, index) => ({
                    id: person.entity_id || `current-${index}`,
                    status: "Current",
                    name: formatPdfDisplay(person.name),
                    role: formatRole(person),
                    birthDate: formatPdfDate(person.birth_date),
                    city: formatPdfDisplay(person.location?.home?.city),
                    from: formatPdfDate(person.start_date),
                    to: formatPdfDate(person.end_date),
                })),
                ...pastPeople.map((person, index) => ({
                    id: person.entity_id || `past-${index}`,
                    status: "Past",
                    name: formatPdfDisplay(person.name),
                    role: formatRole(person),
                    birthDate: formatPdfDate(person.birth_date),
                    city: formatPdfDisplay(person.location?.home?.city),
                    from: formatPdfDate(person.start_date),
                    to: formatPdfDate(person.end_date),
                })),
            ]
        ));
    }

    const kpiRows = Array.isArray(data.financial_kpi) ? [...data.financial_kpi] : [];
    if (hasNonEmptyArray(kpiRows)) {
        kpiRows.sort((a, b) => (b?.year || 0) - (a?.year || 0));
        sections.push(recordsSection(
            "Financial KPIs",
            [
                { key: "year", label: "Year", width: "8%" },
                { key: "revenue", label: "Revenue", width: "14%" },
                { key: "netIncome", label: "Net Income", width: "14%" },
                { key: "employees", label: "Employees", width: "10%" },
                { key: "totalAssets", label: "Total Assets", width: "14%" },
                { key: "materialExpenses", label: "Material Expenses", width: "16%" },
                { key: "personnelExpenses", label: "Personnel Expenses", width: "16%" },
            ],
            kpiRows.map((row, index) => ({
                id: row.year || `kpi-${index}`,
                year: formatPdfDisplay(row.year),
                revenue: formatMoney(row.revenue),
                netIncome: formatMoney(row.net_income),
                employees: formatPdfDisplay(row.employees),
                totalAssets: formatMoney(row.active_total),
                materialExpenses: formatMoney(row.material_expenses),
                personnelExpenses: formatMoney(row.personnel_expenses),
            }))
        ));
    }

    const shareholderEntries = data?.shareholders?.entries || [];
    if (hasNonEmptyArray(shareholderEntries)) {
        const totalCapital = data.shareholders.total_capital;
        if (totalCapital) {
            sections.push(keyValueSection("Shareholders", [
                {
                    title: "Total Capital",
                    details: formatMoney(totalCapital.amount, totalCapital.currency),
                },
            ]));
        }

        sections.push(recordsSection(
            "Shareholders",
            [
                { key: "name", label: "Name", width: "24%" },
                { key: "role", label: "Role", width: "16%" },
                { key: "contribution", label: "Contribution", width: "18%" },
                { key: "ownership", label: "Ownership", width: "14%" },
                { key: "address", label: "Address", width: "28%" },
            ],
            shareholderEntries.map((entry, index) => ({
                id: entry?.shareholder?.entity_id || `shareholder-${index}`,
                name: formatShareholderName(entry),
                role: formatShareholderRole(entry),
                contribution: entry?.contribution
                    ? formatMoney(entry.contribution.amount, entry.contribution.currency)
                    : "-",
                ownership: formatRatio(entry?.contribution_ratio),
                address: formatShareholderAddress(entry?.shareholder?.address),
            }))
        ));
    }

    const uboOwners = data?.ubos?.beneficial_owners || [];
    if (hasNonEmptyArray(uboOwners)) {
        sections.push(recordsSection(
            "Beneficial Owners",
            [
                { key: "name", label: "Name", width: "22%" },
                { key: "birthDate", label: "Date of Birth", width: "16%" },
                { key: "city", label: "City", width: "14%" },
                { key: "ownership", label: "Ownership", width: "14%" },
                { key: "path", label: "Path", width: "34%" },
            ],
            uboOwners.map((owner, index) => {
                const person = owner.person || {};
                const path = (owner.paths || [])
                    .map((p) => (p.via || []).map((v) => v.name).filter(Boolean).join(" → "))
                    .filter(Boolean)
                    .join("; ");
                return {
                    id: person.entity_id || `ubo-${index}`,
                    name: formatPdfDisplay(person.name),
                    birthDate: formatPdfDisplay(person.birth_date),
                    city: formatPdfDisplay(person.location?.home?.city),
                    ownership: formatPct(owner.ownership_percentage),
                    path: path || "-",
                };
            })
        ));
    }

    const uboUnresolved = data?.ubos?.unresolved_beneficial_owners || [];
    if (hasNonEmptyArray(uboUnresolved)) {
        sections.push(recordsSection(
            "Unresolved Beneficial Owners",
            [
                { key: "name", label: "Name", width: "28%" },
                { key: "legalForm", label: "Legal Form", width: "22%" },
                { key: "ownership", label: "Ownership", width: "16%" },
                { key: "reason", label: "Reason", width: "34%" },
            ],
            uboUnresolved.map((item, index) => ({
                id: item.entity_id || `unresolved-ubo-${index}`,
                name: formatPdfDisplay(item.name),
                legalForm: formatPdfDisplay(item.legal_form),
                ownership: formatPct(item.ownership_percentage),
                reason: formatPdfDisplay(item.reason),
            }))
        ));
    }

    const holdings = data?.shareholdings?.holdings?.current || [];
    if (hasNonEmptyArray(holdings)) {
        sections.push(recordsSection(
            "Shareholdings",
            [
                { key: "name", label: "Company Name", width: "20%" },
                { key: "legalForm", label: "Legal Form", width: "12%" },
                { key: "status", label: "Status", width: "10%" },
                { key: "ownership", label: "Ownership", width: "12%" },
                { key: "contribution", label: "Contribution", width: "14%" },
                { key: "asOf", label: "As Of", width: "12%" },
                { key: "address", label: "Address", width: "20%" },
            ],
            holdings.map((item, index) => {
                const org = item.organization || {};
                const ownership = item.ownership || {};
                return {
                    id: org.entity_id || `holding-${index}`,
                    name: formatPdfDisplay(org.name),
                    legalForm: formatPdfDisplay(org.legal_form),
                    status: formatPdfDisplay(org.status),
                    ownership: ownership.percentage !== undefined && ownership.percentage !== null
                        ? `${ownership.percentage}%`
                        : "-",
                    contribution: ownership.contribution
                        ? formatMoney(ownership.contribution.amount, ownership.contribution.currency)
                        : "-",
                    asOf: formatPdfDisplay(item.as_of),
                    address: formatAddress(org.address),
                };
            })
        ));
    }

    const history = data?.history || [];
    if (hasNonEmptyArray(history)) {
        sections.push(recordsSection(
            "Publications & Register History",
            [
                { key: "date", label: "Date", width: "14%" },
                { key: "event", label: "Event", width: "20%" },
                { key: "description", label: "Description", width: "28%" },
                { key: "details", label: "Details", width: "38%" },
            ],
            history.map((item, index) => {
                const shortDescription = item.description?.short
                    ? formatI18nLabel(item.description.short)
                    : null;
                return {
                    id: `history-${index}`,
                    date: formatPdfDate(item.start_date),
                    event: formatI18nLabel(item.name),
                    description: (shortDescription && shortDescription !== "-")
                        ? shortDescription
                        : formatI18nLabel(item.description),
                    details: formatHistoryDetails(item),
                };
            })
        ));
    }

    const insolvency = data?.insolvency_publications || [];
    if (hasNonEmptyArray(insolvency)) {
        sections.push(recordsSection(
            "Insolvency Publications",
            [
                { key: "date", label: "Date", width: "12%" },
                { key: "court", label: "Court", width: "14%" },
                { key: "caseId", label: "Case ID", width: "14%" },
                { key: "label", label: "Label", width: "16%" },
                { key: "register", label: "Register", width: "14%" },
                { key: "notice", label: "Notice", width: "30%" },
            ],
            insolvency.map((row, index) => ({
                id: row.insolvency_id || `insolvency-${index}`,
                date: formatPdfDate(row.publication_date),
                court: formatPdfDisplay(row.court_city),
                caseId: formatPdfDisplay(row.insolvency_id),
                label: formatPdfDisplay(row.publication_label),
                register: formatPdfDisplay(row.register),
                notice: formatPdfDisplay(row.event_notice),
            }))
        ));
    }

    const statements = data?.annual_financial_statements || [];
    if (hasNonEmptyArray(statements)) {
        sections.push(recordsSection(
            "Annual Financial Statements",
            [
                { key: "year", label: "Year", width: "12%" },
                { key: "title", label: "Title", width: "34%" },
                { key: "type", label: "Type", width: "18%" },
                { key: "date", label: "Date", width: "18%" },
                { key: "language", label: "Language", width: "18%" },
            ],
            statements.map((row, index) => ({
                id: `${row.year}-${index}`,
                year: formatPdfDisplay(row.year),
                title: formatPdfDisplay(row.document_title),
                type: formatPdfDisplay(row.document_type),
                date: formatPdfDate(row.document_date),
                language: formatPdfDisplay(row.language),
            }))
        ));

        const mdDocs = statements.filter((row) => row.document_md).slice(0, 3);
        mdDocs.forEach((row, index) => {
            sections.push(keyValueSection(
                `Annual Statement Document (${row.year || index + 1})`,
                [
                    { title: "Title", details: formatPdfDisplay(row.document_title) },
                    { title: "Content", details: String(row.document_md) },
                ]
            ));
        });
    }

    const news = data?.news || [];
    if (hasNonEmptyArray(news)) {
        sections.push(recordsSection(
            "News",
            [
                { key: "date", label: "Date", width: "16%" },
                { key: "title", label: "Title", width: "40%" },
                { key: "source", label: "Source", width: "20%" },
                { key: "link", label: "Link", width: "24%" },
            ],
            news.map((row, index) => ({
                id: row.id || `news-${index}`,
                date: formatPdfDate(row.publication_date),
                title: formatPdfDisplay(row.title),
                source: formatPdfDisplay(row.source),
                link: formatPdfDisplay(row.url),
            }))
        ));
    }

    const transactions = data?.mergers_and_acquisitions?.transactions || [];
    if (hasNonEmptyArray(transactions)) {
        sections.push(recordsSection(
            "Mergers & Acquisitions",
            [
                { key: "date", label: "Date", width: "12%" },
                { key: "headline", label: "Headline", width: "24%" },
                { key: "type", label: "Type", width: "14%" },
                { key: "role", label: "Role", width: "14%" },
                { key: "phase", label: "Phase", width: "12%" },
                { key: "counterparties", label: "Counterparties", width: "24%" },
            ],
            transactions.map((tx, index) => {
                const kindName = tx.kind?.name
                    ? formatI18nLabel(tx.kind.name)
                    : null;
                return {
                    id: tx.id || `ma-${index}`,
                    date: formatPdfDate(tx.date),
                    headline: formatI18nLabel(tx.headline),
                    type: (kindName && kindName !== "-")
                        ? kindName
                        : formatI18nLabel(tx.type?.name),
                    role: formatMaRole(tx.role),
                    phase: formatPdfDisplay(tx.phase),
                    counterparties: (tx.counterparties || [])
                        .map((c) => c.name)
                        .filter(Boolean)
                        .join(", ") || "-",
                };
            })
        ));
    }

    const connections = data?.network?.connections || [];
    if (hasNonEmptyArray(connections)) {
        sections.push(recordsSection(
            "Relationship Network",
            [
                { key: "source", label: "Source", width: "16%" },
                { key: "target", label: "Target", width: "16%" },
                { key: "type", label: "Type", width: "14%" },
                { key: "role", label: "Role", width: "16%" },
                { key: "from", label: "From", width: "12%" },
                { key: "to", label: "To", width: "12%" },
                { key: "current", label: "Current", width: "14%" },
            ],
            connections.map((conn, index) => ({
                id: `network-${index}`,
                source: formatPdfDisplay(conn.source?.name),
                target: formatPdfDisplay(conn.target?.name),
                type: formatPdfDisplay(conn.connection_type || conn.label),
                role: conn.role?.en?.long || conn.role?.de?.long || conn.label || "-",
                from: formatPdfDisplay(conn.start_date),
                to: formatPdfDisplay(conn.end_date),
                current: formatPdfYesNo(!!conn.is_current),
            }))
        ));
    }

    return sections.filter(Boolean);
};
